import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar, Clock, User, Phone, Mail, MessageSquare, Trash2, ArrowLeft, CheckCircle, AlertCircle, Edit, Calendar as CalendarIcon, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { SettingsMenu } from '@/components/SettingsMenu';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeSlots = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
  '20:00', '20:30', '21:00'
];

const durationOptions = [
  { value: 30, label: '30 Minuten' },
  { value: 60, label: '1 Stunde' },
  { value: 90, label: '1,5 Stunden' },
  { value: 120, label: '2 Stunden' },
  { value: 150, label: '2,5 Stunden' },
  { value: 180, label: '3 Stunden' },
  { value: 210, label: '3,5 Stunden' },
  { value: 240, label: '4 Stunden' },
  { value: 270, label: '4,5 Stunden' },
  { value: 300, label: '5 Stunden' },
];

const statusConfig = {
  confirmed: {
    color: 'bg-success/10 text-success border-success/20',
    icon: CheckCircle,
    label: 'Bestätigt',
  },
  pending: {
    color: 'bg-warning/10 text-warning border-warning/20',
    icon: Clock,
    label: 'Ausstehend',
  },
  cancelled: {
    color: 'bg-muted/10 text-muted-foreground border-muted/20',
    icon: AlertCircle,
    label: 'Abgesagt',
  },
  completed: {
    color: 'bg-info/10 text-info border-info/20',
    icon: CheckCircle,
    label: 'Abgeschlossen',
  },
};

export default function MeineTermine() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editAppointment, setEditAppointment] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    email: '',
    date: null,
    time: '',
    duration: 60,
    service: ''
  });
  const [editDatePickerOpen, setEditDatePickerOpen] = useState(false);
  const [googleAuthStatus, setGoogleAuthStatus] = useState({ is_authenticated: false });
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    fetchAppointments();
    checkGoogleAuth();
  }, []);

  const checkGoogleAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/status`);
      setGoogleAuthStatus(response.data);
    } catch (error) {
      console.error('Error checking Google auth:', error);
      setGoogleAuthStatus({ is_authenticated: false });
    } finally {
      setAuthChecked(true);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Fehler beim Google Login');
    }
  };

  useEffect(() => {
    // Filter appointments based on search query
    if (!searchQuery.trim()) {
      setFilteredAppointments(appointments);
    } else {
      const query = searchQuery.toLowerCase();
      // Remove spaces for phone number comparison
      const queryNoSpaces = query.replace(/\s/g, '');
      
      const filtered = appointments.filter(apt => {
        const phone = apt.phone || '';
        const phoneNoSpaces = phone.replace(/\s/g, '');

        return (
          apt.name.toLowerCase().includes(query) ||
          (phoneNoSpaces && phoneNoSpaces.includes(queryNoSpaces)) ||
          (phone && phone.includes(query)) ||
          (apt.email && apt.email.toLowerCase().includes(query)) ||
          (apt.service && apt.service.toLowerCase().includes(query)) ||
          apt.date.includes(query)
        );
      });
      setFilteredAppointments(filtered);
    }
  }, [searchQuery, appointments]);

  const fetchAppointments = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/appointments`);
      
      // Get today's date at midnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Filter out past appointments and sort by date
      const futureAppointments = response.data.filter(apt => {
        const aptDate = new Date(apt.date + 'T00:00:00');
        return aptDate >= today;
      });
      
      const sorted = futureAppointments.sort((a, b) => {
        const dateA = new Date(a.date + 'T' + a.time);
        const dateB = new Date(b.date + 'T' + b.time);
        return dateA - dateB;
      });
      
      setAppointments(sorted);
      setFilteredAppointments(sorted);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Fehler beim Laden der Termine');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAppointment = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success('Termin wurde gelöscht und aus Google Calendar entfernt');
      fetchAppointments();
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Fehler beim Löschen des Termins');
    }
  };

  const handleEditClick = (appointment) => {
    const [year, month, day] = appointment.date.split('-');
    const dateObj = new Date(year, month - 1, day);
    
    setEditAppointment(appointment);
    setEditForm({
      name: appointment.name,
      phone: appointment.phone || '',
      email: appointment.email || '',
      date: dateObj,
      time: appointment.time,
      duration: appointment.duration,
      service: appointment.service || ''
    });
  };

  const handleEditSave = async () => {
    if (!editForm.name || !editForm.date || !editForm.time) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    if (!editForm.phone.trim() && !editForm.email.trim()) {
      toast.error('Bitte Telefonnummer oder E-Mail-Adresse angeben');
      return;
    }

    if (editForm.email.trim() && !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(editForm.email.trim())) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }

    try {
      const formattedDate = format(editForm.date, 'yyyy-MM-dd');
      await axios.put(`${API}/appointments/${editAppointment.id}`, {
        name: editForm.name,
        phone: editForm.phone.trim() || null,
        email: editForm.email.trim() || null,
        date: formattedDate,
        time: editForm.time,
        duration: editForm.duration,
        service: editForm.service || null
      });
      
      toast.success('Termin wurde aktualisiert (inkl. Google Calendar)');
      setEditAppointment(null);
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Fehler beim Aktualisieren des Termins');
    }
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return format(date, 'dd.MM.yyyy (EEEE)', { locale: de });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 h-14 bg-background/95 backdrop-blur-xl border-b border-border/50 px-4" data-testid="termine-header">
        <div className="h-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="-ml-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold font-heading text-primary" data-testid="termine-title">
                Meine Termine
              </h1>
              <p className="text-xs text-muted-foreground">{appointments.length} Termine</p>
            </div>
          </div>
          <SettingsMenu />
        </div>
      </header>

      {/* Google Calendar Warning Banner */}
      {authChecked && !googleAuthStatus.is_authenticated && (
        <div className="mx-4 mt-3" data-testid="google-warning-banner">
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive font-medium">
                Google Calendar nicht verbunden!
              </p>
            </div>
            <Button
              onClick={handleGoogleLogin}
              size="sm"
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground text-xs flex-shrink-0"
              data-testid="google-reconnect-button"
            >
              Verbinden
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4" data-testid="termine-main">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Input
              type="text"
              placeholder="Suchen nach Name, Telefon, Leistung..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-secondary border-border focus:border-primary focus:ring-2 focus:ring-primary/20 h-12 text-base pl-4 pr-4"
              data-testid="search-input"
            />
            {searchQuery && (
              <p className="text-xs text-muted-foreground mt-2">
                {filteredAppointments.length} von {appointments.length} Terminen
              </p>
            )}
          </div>

          {/* Appointments List */}
          {isLoading ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground animate-spin mb-4" />
              <p className="text-muted-foreground">Termine werden geladen...</p>
            </div>
          ) : filteredAppointments.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchQuery ? 'Keine Termine gefunden' : 'Keine Termine vorhanden'}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchQuery ? 'Versuchen Sie eine andere Suche' : 'Erstellen Sie Ihren ersten Termin'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90">
                    Termin erstellen
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="appointments-list">
              {filteredAppointments.map((appointment) => {
                const status = statusConfig[appointment.status] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <Card
                    key={appointment.id}
                    className="bg-card border border-border hover:border-primary/50 transition-all duration-300"
                    data-testid="appointment-card"
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header with Name and Status */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-base flex items-center gap-2" data-testid="appointment-name">
                              <User className="w-4 h-4 text-primary" />
                              {appointment.name}
                            </h3>
                            {appointment.phone && (
                              <p className="text-sm text-muted-foreground font-mono mt-1 flex items-center gap-2" data-testid="appointment-phone">
                                <Phone className="w-3.5 h-3.5" />
                                {appointment.phone}
                              </p>
                            )}
                            {appointment.email && (
                              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2 break-all" data-testid="appointment-email">
                                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                                {appointment.email}
                              </p>
                            )}
                          </div>
                          <Badge className={`${status.color} inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border`} data-testid="appointment-status">
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </Badge>
                        </div>

                        {/* Date and Time */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-foreground" data-testid="appointment-date">
                            <Calendar className="w-4 h-4 text-primary" />
                            <span className="font-medium">{formatDate(appointment.date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground" data-testid="appointment-time">
                            <Clock className="w-4 h-4 text-primary" />
                            <span>{appointment.time} Uhr • {appointment.duration} Minuten</span>
                          </div>
                        </div>

                        {/* Service */}
                        {appointment.service && (
                          <div className="p-3 bg-secondary/30 rounded border border-border" data-testid="appointment-service">
                            <div className="flex items-start gap-2 text-sm">
                              <MessageSquare className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <p className="text-foreground">{appointment.service}</p>
                            </div>
                          </div>
                        )}

                        {/* Edit and Delete Buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditClick(appointment)}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-primary/50 text-primary hover:bg-primary/10"
                            data-testid="edit-button"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </Button>
                          <Button
                            onClick={() => setDeleteId(appointment.id)}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            data-testid="delete-button"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Löschen
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Termin löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Der Termin wird aus der App UND aus Ihrem Google Calendar gelöscht. Die geplante SMS-Erinnerung wird ebenfalls storniert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel className="border-border w-full sm:w-auto">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteAppointment(deleteId)}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground w-full sm:w-auto"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAppointment} onOpenChange={() => setEditAppointment(null)}>
        <DialogContent className="bg-card border-border mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Termin bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                Kundenname
              </Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-secondary border-border focus:border-primary h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Telefonnummer
              </Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="bg-secondary border-border focus:border-primary h-11 font-mono"
                type="tel"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                E-Mail (optional)
              </Label>
              <Input
                id="edit-email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-secondary border-border focus:border-primary h-11"
                placeholder="kunde@beispiel.de"
                type="email"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                Datum
              </Label>
              <Popover open={editDatePickerOpen} onOpenChange={setEditDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left bg-secondary border-border hover:border-primary h-11"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editForm.date ? format(editForm.date, 'dd.MM.yyyy', { locale: de }) : 'Datum wählen'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={editForm.date}
                    onSelect={(selectedDate) => {
                      setEditForm({ ...editForm, date: selectedDate });
                      setEditDatePickerOpen(false);
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    className="bg-card"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Uhrzeit
              </Label>
              <Select value={editForm.time} onValueChange={(time) => setEditForm({ ...editForm, time })}>
                <SelectTrigger className="bg-secondary border-border focus:border-primary h-11">
                  <SelectValue placeholder="Uhrzeit wählen" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-60">
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>
                      {slot} Uhr
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Dauer
              </Label>
              <Select value={editForm.duration.toString()} onValueChange={(val) => setEditForm({ ...editForm, duration: parseInt(val) })}>
                <SelectTrigger className="bg-secondary border-border focus:border-primary h-11">
                  <SelectValue placeholder="Dauer wählen" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {durationOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-service" className="text-xs font-subheading uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Leistung / Nachricht
              </Label>
              <Textarea
                id="edit-service"
                value={editForm.service}
                onChange={(e) => setEditForm({ ...editForm, service: e.target.value })}
                className="bg-secondary border-border focus:border-primary min-h-[80px]"
                placeholder="z.B. Stage 1 Chiptuning für BMW 320d"
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setEditAppointment(null)}
              className="w-full sm:w-auto border-border"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleEditSave}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}